import { masIdsEqual } from './mas-ids';

/** Minimal attendance row for enroll/remove/dedupe/sync mutations. */
export interface AttendanceMutationRow {
	id: string;
	attended?: boolean;
	name?: string;
	notifications?: unknown;
	reserved?: boolean;
	scheduleId?: string;
	/** Skill/category for `eventType: lesson` (my Programs → Lessons). */
	lessonCategory?: string;
}

/** Minimal wait-list row for add/remove mutations. */
export interface WaitListMutationRow {
	accountId: string;
	addedAt?: string;
	name?: string;
	eventId?: string;
}

export type AttendanceArraysMutationResult<TAtt, TWait> = {
	attendance: TAtt[];
	waitList: TWait[];
	changed: boolean;
};

export function removeAttendanceMember<TAtt extends AttendanceMutationRow>(
	attendance: readonly TAtt[],
	memberId: string,
): { attendance: TAtt[]; changed: boolean } {
	const idx = attendance.findIndex(a => masIdsEqual(a.id, memberId));
	if (idx < 0) {
		return { attendance: [...attendance], changed: false };
	}
	const next = [...attendance];
	next.splice(idx, 1);
	return { attendance: next, changed: true };
}

export function addWaitListEntry<TWait extends WaitListMutationRow>(
	waitList: readonly TWait[],
	entry: TWait,
): { waitList: TWait[]; changed: boolean } {
	if (waitList.some(w => masIdsEqual(w.accountId, entry.accountId))) {
		return { waitList: [...waitList], changed: false };
	}
	return { waitList: [...waitList, entry], changed: true };
}

export function removeWaitListMember<TWait extends WaitListMutationRow>(
	waitList: readonly TWait[],
	memberId: string,
): { waitList: TWait[]; changed: boolean } {
	const before = waitList.length;
	const next = waitList.filter(w => !masIdsEqual(w.accountId, memberId));
	return { waitList: next, changed: next.length !== before };
}

export function dedupeAttendanceMember<TAtt extends AttendanceMutationRow>(
	attendance: readonly TAtt[],
	memberId: string,
	opts?: { memberName?: string; notifications?: TAtt['notifications'] },
): { attendance: TAtt[]; changed: boolean } {
	const matchingIndices = attendance
		.map((entry, idx) => ({ entry, idx }))
		.filter(row => masIdsEqual(row.entry.id, memberId));

	if (matchingIndices.length <= 1) {
		return { attendance: [...attendance], changed: false };
	}

	const primary = matchingIndices[0];
	const merged = {
		...primary.entry,
		attended: matchingIndices.some(row => !!row.entry.attended),
		name: opts?.memberName ?? primary.entry.name,
		notifications: opts?.notifications ?? primary.entry.notifications,
		reserved: matchingIndices.some(row => !!row.entry.reserved),
	} as TAtt;

	const primaryIdx = primary.idx;
	const next = attendance.filter((entry, idx) => !masIdsEqual(entry.id, memberId) || idx === primaryIdx);
	next[primaryIdx] = merged;
	return { attendance: next, changed: true };
}

export function syncAttendanceMemberName<TAtt extends AttendanceMutationRow>(
	attendance: readonly TAtt[],
	memberId: string,
	memberName?: string,
): { attendance: TAtt[]; changed: boolean } {
	let changed = false;
	const next = attendance.map(entry => {
		if (!masIdsEqual(entry.id, memberId)) {
			return entry;
		}
		if (!memberName || entry.name === memberName) {
			return entry;
		}
		changed = true;
		return { ...entry, name: memberName };
	});
	return { attendance: next, changed };
}

export function syncAttendanceNotificationName<TAtt extends AttendanceMutationRow>(
	attendance: readonly TAtt[],
	memberId: string,
	memberName?: string,
): { attendance: TAtt[]; changed: boolean } {
	let changed = false;
	const next = attendance.map(entry => {
		const notifications = entry.notifications as { id?: string; name?: string } | undefined;
		if (!notifications || !masIdsEqual(notifications.id, memberId)) {
			return entry;
		}
		if (!memberName || notifications.name === memberName) {
			return entry;
		}
		changed = true;
		return {
			...entry,
			notifications: {
				...(notifications as object),
				name: memberName,
			},
		} as TAtt;
	});
	return { attendance: next, changed };
}

export interface NotificationPrefsPatch {
	id: string;
	name?: string;
	email?: string;
	phone?: string;
}

/** Updates email/phone (and name) on attendance rows whose nested notification id matches the parent account. */
export function syncAttendanceNotificationPrefs<TAtt extends AttendanceMutationRow>(
	attendance: readonly TAtt[],
	parentId: string,
	patch: NotificationPrefsPatch,
): { attendance: TAtt[]; changed: boolean } {
	let changed = false;
	const next = attendance.map(entry => {
		const notifications = entry.notifications as NotificationPrefsPatch | undefined;
		if (!notifications || !masIdsEqual(notifications.id, parentId)) {
			return entry;
		}
		const updated: NotificationPrefsPatch = {
			...notifications,
			id: patch.id,
			name: patch.name ?? notifications.name,
			email: patch.email,
			phone: patch.phone,
		};
		if (
			updated.name === notifications.name &&
			updated.email === notifications.email &&
			updated.phone === notifications.phone
		) {
			return entry;
		}
		changed = true;
		return { ...entry, notifications: updated } as TAtt;
	});
	return { attendance: next, changed };
}

/** Updates top-level schedule `notifications[]` entries for Firestore query consistency. */
export function syncScheduleNotificationsArray<TNotif extends NotificationPrefsPatch>(
	scheduleNotifications: readonly TNotif[] | undefined,
	parentId: string,
	patch: NotificationPrefsPatch,
): { notifications: TNotif[]; changed: boolean } {
	if (!scheduleNotifications?.length) {
		return { notifications: scheduleNotifications ? [...scheduleNotifications] : [], changed: false };
	}

	let changed = false;
	const next = scheduleNotifications.map(entry => {
		if (!masIdsEqual(entry.id, parentId)) {
			return entry;
		}
		const updated: TNotif = {
			...entry,
			id: patch.id,
			name: patch.name ?? entry.name,
			email: patch.email,
			phone: patch.phone,
		};
		if (updated.name === entry.name && updated.email === entry.email && updated.phone === entry.phone) {
			return entry;
		}
		changed = true;
		return updated;
	});
	return { notifications: next, changed };
}

export function upsertAttendanceMember<TAtt extends AttendanceMutationRow>(
	attendance: readonly TAtt[],
	memberId: string,
	scheduleId: string,
	patch: {
		attended?: boolean;
		memberName?: string;
		notifications?: TAtt['notifications'];
		reserved?: boolean;
		lessonCategory?: string;
	},
): { attendance: TAtt[]; changed: boolean } {
	const idx = attendance.findIndex(a => masIdsEqual(a.id, memberId));

	if (idx >= 0) {
		const current = attendance[idx];
		const next = [...attendance];
		next[idx] = {
			...current,
			attended: patch.attended ?? current.attended,
			name: patch.memberName ?? current.name,
			notifications: patch.notifications ?? current.notifications,
			reserved: patch.reserved ?? current.reserved,
			lessonCategory: patch.lessonCategory ?? current.lessonCategory,
		};
		return { attendance: next, changed: true };
	}

	return {
		attendance: [
			...attendance,
			{
				attended: patch.attended ?? false,
				id: memberId,
				name: patch.memberName ?? '',
				notifications: patch.notifications,
				reserved: patch.reserved ?? true,
				scheduleId,
				lessonCategory: patch.lessonCategory,
			} as TAtt,
		],
		changed: true,
	};
}
